/**
 * Workflow Action Cache Types
 *
 * Types for caching browser actions from workflow runs for fast replay
 * without the AI agent.
 */

import { z } from 'zod';
import { AssertionSeverityEnum, type AssertionSeverity } from './workflow-node-schemas.js';

// ─────────────────────────────────────────────────────────────────────────────
// Selector Strategy Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Role-based selector (most stable for accessibility-focused apps)
 */
export type RoleSelector = {
  role: string;
  name?: string;
  exact?: boolean;
};

/**
 * Text content selector
 */
export type TextSelector = {
  text: string;
  exact?: boolean;
};

/**
 * Parent context for disambiguating elements with similar selectors
 */
/**
 * Dynamic-text annotation for parent-context fields whose disambiguating text
 * originates from a workflow variable and must be re-resolved at replay time.
 */
export type ParentContextDynamicText = {
  source: 'inputValue' | 'workflowState';
  key: string;
  originalText: string;
};

/**
 * A single node in a parent-scoping chain.
 */
export type ParentContextNode = {
  role: string;
  name?: string;
  css?: string;
  nameFromHeading?: boolean;
  /**
   * Filter the parent by text it contains. Used to anchor on row-like
   * ancestors (role=row/listitem/gridcell) where the row has no accessible
   * name of its own but contains a stable cell value (e.g. a variable name).
   * Applied at replay as `.filter({ hasText: containsText })`.
   */
  containsText?: string;
  /** Dynamic binding for containsText when it originated from a workflow variable. */
  containsTextDynamic?: ParentContextDynamicText;
};

export type ParentContext = {
  /** Single parent role (backward compatible) */
  role?: string;
  /** Single parent name (backward compatible) */
  name?: string;
  /** Optional CSS selector for parent scoping when role-based lookup is unavailable */
  css?: string;
  /** True when name was extracted from a child heading, not the parent's own accessible name */
  nameFromHeading?: boolean;
  /**
   * Filter the parent by text it contains. Used to anchor on row-like
   * ancestors that lack a unique accessible name (see ParentContextNode).
   */
  containsText?: string;
  /** Dynamic binding for containsText when it originated from a workflow variable. */
  containsTextDynamic?: ParentContextDynamicText;

  /** Recursive parent chain for deep disambiguation (innermost to outermost) */
  parentChain?: ParentContextNode[];

  /** Fallback: index of matched element when no unique parent found */
  matchIndex?: number;
};

/**
 * Frame/container context for elements inside iframes or shadow DOM.
 * Captured at recording time to ensure deterministic replay.
 */
export type FrameContext = {
  /** Type of container */
  type: 'iframe' | 'shadow' | 'named-frame';
  /** Selector to locate the container (e.g., 'iframe.designer-client-frame') */
  selector: string;
  /** Optional: frame name attribute for named frames */
  name?: string;
  /** Optional: frame ID attribute */
  id?: string;
};

/** Who authored the active Playwright locator. */
export type SelectorAuthor = 'deterministic' | 'agentic';

/** Alternate Playwright locator candidate stored for comparison/debugging. */
export type PlaywrightLocatorCandidate = {
  /** Locator chain string. */
  locator: string;
  /** Whether the locator contains variable placeholders. */
  usesVariableBinding?: boolean;
  /** Variable key used by the locator, if any. */
  boundVariableKey?: string;
};

/**
 * Multiple selector strategies for fallback during replay.
 * Tried in order: testId -> role -> ariaLabel -> placeholder -> text -> css
 */
export type SelectorStrategy = {
  /** data-testid selector (most stable when available) */
  testId?: string;
  /** Role-based selector (most stable) */
  role?: RoleSelector;
  /** Label text selector (getByLabel) */
  ariaLabel?: string;
  /** Placeholder selector (getByPlaceholder) */
  placeholder?: string;
  /** Text content selector */
  text?: TextSelector;
  /** Additional text parts to require within the resolved element */
  containsText?: string[];
  /** CSS selector (least stable, last resort) */
  css?: string;
  /** Parent context for disambiguation */
  parentContext?: ParentContext;
  /** Frame/container context - element lives inside this container */
  frameContext?: FrameContext;

  // ─────────────────────────────────────────────────────────────────────────────
  // Data attribute disambiguation (for list/table elements)
  // ─────────────────────────────────────────────────────────────────────────────

  /** Item index for list disambiguation (data-item-index, data-selection-index) */
  itemIndex?: number;
  /** Row index for table/grid disambiguation (aria-rowindex) */
  rowIndex?: number;
  /** Automation ID for verification (data-automationid) */
  automationId?: string;

  /** Filter to disambiguate by a child element's role + name (e.g., child img alt text) */
  childFilter?: {
    role: string;
    name: string;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Playwright locator chain (authored by cache selector agent)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Active Playwright locator chain used during replay.
   * Supports variable placeholders: {{var.project_name}} hydrated at replay time.
   * Example: "getByRole('row').filter({ hasText: '{{var.project_name}}' }).getByRole('button', { name: 'Edit' })"
   */
  playwrightLocator?: string;

  /** Alternate deterministic/agentic locator candidates preserved for comparison. */
  playwrightLocatorCandidates?: Partial<Record<SelectorAuthor, PlaywrightLocatorCandidate>>;

  // ─────────────────────────────────────────────────────────────────────────────
  // Dynamic text binding (for selectors containing run-specific content)
  // ─────────────────────────────────────────────────────────────────────────────

  /** Dynamic text binding — selector text that varies between runs */
  dynamicText?: {
    /** Data source for the dynamic value */
    source: 'inputValue' | 'workflowState';
    /** Key in the data source */
    key: string;
    /** The literal text at recording time (for token replacement) */
    originalText: string;
  };
};

const nonBlankStringSchema = z.string().refine((value) => value.trim().length > 0, {
  message: 'Expected a non-empty string',
});

const parentContextDynamicTextSchema = z.strictObject({
  source: z.enum(['inputValue', 'workflowState']),
  key: nonBlankStringSchema,
  originalText: nonBlankStringSchema,
}) satisfies z.ZodType<ParentContextDynamicText>;

const parentContextNodeSchema = z.strictObject({
  role: nonBlankStringSchema,
  name: nonBlankStringSchema.optional(),
  css: nonBlankStringSchema.optional(),
  nameFromHeading: z.boolean().optional(),
  containsText: nonBlankStringSchema.optional(),
  containsTextDynamic: parentContextDynamicTextSchema.optional(),
}) satisfies z.ZodType<ParentContextNode>;

const parentContextSchema = z.strictObject({
  role: nonBlankStringSchema.optional(),
  name: nonBlankStringSchema.optional(),
  css: nonBlankStringSchema.optional(),
  nameFromHeading: z.boolean().optional(),
  containsText: nonBlankStringSchema.optional(),
  containsTextDynamic: parentContextDynamicTextSchema.optional(),
  parentChain: z.array(parentContextNodeSchema).optional(),
  matchIndex: z.number().int().nonnegative().optional(),
}) satisfies z.ZodType<ParentContext>;

const frameContextSchema = z.strictObject({
  type: z.enum(['iframe', 'shadow', 'named-frame']),
  selector: nonBlankStringSchema,
  name: nonBlankStringSchema.optional(),
  id: nonBlankStringSchema.optional(),
}) satisfies z.ZodType<FrameContext>;

const playwrightLocatorCandidateSchema = z.strictObject({
  locator: nonBlankStringSchema,
  usesVariableBinding: z.boolean().optional(),
  boundVariableKey: nonBlankStringSchema.optional(),
}) satisfies z.ZodType<PlaywrightLocatorCandidate>;

/**
 * Runtime schema for selector artifacts crossing cache boundaries.
 *
 * Keep this beside {@link SelectorStrategy}: the optimizer route, recipe
 * builder, and replay validator all consume the same schema so a selector
 * accepted at commit cannot fail later because its JSON value shapes drifted.
 */
export const selectorStrategySchema = z
  .strictObject({
    testId: nonBlankStringSchema.optional(),
    role: z
      .strictObject({
        role: nonBlankStringSchema,
        name: nonBlankStringSchema.optional(),
        exact: z.boolean().optional(),
      })
      .optional(),
    ariaLabel: nonBlankStringSchema.optional(),
    placeholder: nonBlankStringSchema.optional(),
    text: z
      .strictObject({
        text: nonBlankStringSchema,
        exact: z.boolean().optional(),
      })
      .optional(),
    containsText: z.array(nonBlankStringSchema).min(1).optional(),
    css: nonBlankStringSchema.optional(),
    parentContext: parentContextSchema.optional(),
    frameContext: frameContextSchema.optional(),
    itemIndex: z.number().int().nonnegative().optional(),
    rowIndex: z.number().int().nonnegative().optional(),
    automationId: nonBlankStringSchema.optional(),
    childFilter: z
      .strictObject({
        role: nonBlankStringSchema,
        name: nonBlankStringSchema,
      })
      .optional(),
    playwrightLocator: nonBlankStringSchema.optional(),
    playwrightLocatorCandidates: z
      .strictObject({
        deterministic: playwrightLocatorCandidateSchema.optional(),
        agentic: playwrightLocatorCandidateSchema.optional(),
      })
      .optional(),
    dynamicText: parentContextDynamicTextSchema.optional(),
  })
  .superRefine((selectors, ctx) => {
    const hasLocatorCandidate = Object.values(selectors.playwrightLocatorCandidates ?? {}).some(
      (candidate) => candidate?.locator.trim().length
    );
    const hasExecutableSelector = Boolean(
      selectors.testId ||
      selectors.role ||
      selectors.ariaLabel ||
      selectors.placeholder ||
      selectors.text ||
      selectors.css ||
      selectors.playwrightLocator ||
      hasLocatorCandidate
    );
    if (!hasExecutableSelector) {
      ctx.addIssue({
        code: 'custom',
        message: 'Expected at least one executable selector',
      });
    }
  }) satisfies z.ZodType<SelectorStrategy>;

// ─────────────────────────────────────────────────────────────────────────────
// Context Extractor Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A small JS extraction script that runs via page.evaluate() after cache replay
 * to extract fresh application-generated values (IDs, codes, computed results) from the live page.
 *
 * Should only target data produced by the application (auto-generated IDs, confirmation codes,
 * computed totals), NOT data the agent entered (form inputs, search terms).
 *
 * Authored by agents in debug mode when step notes contain run-specific data.
 */
export type ContextExtractor = {
  /** Snake_case key for the extracted value (e.g., "project_id") */
  key: string;
  /** JS expression for page.evaluate(). Must return string | null. */
  expression: string;
  /** Human-readable description of what this extractor captures */
  description?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Cached Data Script Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A Python script cached for automatic replay alongside browser action cache.
 * When the agent uses python_execute with cache_label, the script is stored here
 * so it can be re-run without the agent during cached replay.
 */
export type CachedDataScript = {
  /** Short descriptive label (e.g., "validate-row-count") */
  label: string;
  /** Python code to execute */
  code: string;
  /** Table name from the preceding browser_extract_table call */
  tableName: string;
  /** Optional ref for a specific table (matches browser_extract_table ref parameter) */
  tableRef?: string;
  /** ISO timestamp when this script was cached */
  createdAt: string;
  /** Index of the corresponding CachedActionRecord (for playbook linkage). */
  actionIndex?: number;
};

export const cachedDataScriptSchema = z.strictObject({
  label: nonBlankStringSchema,
  code: nonBlankStringSchema,
  tableName: nonBlankStringSchema,
  tableRef: nonBlankStringSchema.optional(),
  createdAt: nonBlankStringSchema,
  actionIndex: z.number().int().min(0).optional(),
}) satisfies z.ZodType<CachedDataScript>;

/**
 * Result of replaying a cached data script during cache replay.
 */
export interface DataScriptReplayResult {
  label: string;
  success: boolean;
  stdout?: string;
  stderr?: string;
  error?: string;
  durationMs: number;
}

export type CacheCaptureWarningReason =
  | 'empty'
  | 'invalid'
  | 'error'
  | 'timeout'
  | 'missing_callback';

export type CacheCaptureWarning = {
  key: string;
  required: boolean;
  reason: CacheCaptureWarningReason;
  actionIndex?: number;
  expression?: string;
  currentUrl?: string;
  lastValue?: string;
  validationReason?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Cached Action Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Types of browser actions that can be cached
 */
export type CachedActionType =
  | 'navigate'
  | 'click'
  | 'type'
  | 'press_key'
  | 'select_option'
  | 'fill_form'
  | 'wait_for'
  | 'hover'
  | 'drag'
  | 'resize'
  | 'scroll'
  | 'screenshot'
  | 'file_upload'
  | 'handle_dialog'
  | 'snapshot'
  | 'evaluate'
  | 'annotation'
  | 'assert'
  | 'move'
  | 'capture'
  | 'extract_table'
  | 'python_execute'
  // Ref-less fast-drive: a self-contained Playwright snippet the L2 agent
  // authored to reproduce a single playbook step. Stored as `CachedAction.code`
  // and replayed via the shared run_code primitive. See `code` below.
  | 'run_code'
  // MCP workflow nodes: one recorded remote MCP tool call (`toolName` is the
  // REMOTE tool name, `args` the recorded arguments with `$var.<key>` tokens).
  // Replays via the dedicated all-or-nothing mcp lane
  // (apps/api/src/workflows/cache/mcp-replay.ts), never the browser runtime.
  | 'mcp_tool_call';

/**
 * Action types the cache replay runtime executes deterministically.
 *
 * This tuple is the admission/replay source of truth. Inert historical
 * observation actions (`snapshot`, `evaluate`) remain valid stored
 * `CachedActionType`s but cannot be authored as executable recipe steps.
 */
export const CACHE_REPLAYABLE_ACTION_TYPES = [
  'navigate',
  'click',
  'type',
  'press_key',
  'select_option',
  'fill_form',
  'wait_for',
  'hover',
  'resize',
  'scroll',
  'screenshot',
  'file_upload',
  'handle_dialog',
  'annotation',
  'assert',
  'move',
  'capture',
  'extract_table',
  'python_execute',
  'run_code',
  'mcp_tool_call',
] as const satisfies readonly CachedActionType[];

export type CacheReplayableActionType = (typeof CACHE_REPLAYABLE_ACTION_TYPES)[number];

export const cacheReplayableActionTypeSchema = z.enum(CACHE_REPLAYABLE_ACTION_TYPES);

/**
 * Types of wait strategies before executing an action
 */
export type WaitStrategyType = 'network_idle' | 'dom_stable' | 'selector' | 'delay';

/**
 * Wait strategy configuration
 */
export type WaitStrategy = {
  type: WaitStrategyType;
  /** CSS selector to wait for (when type is 'selector') */
  selector?: string;
  /** Delay in milliseconds (when type is 'delay') */
  delayMs?: number;
};

/**
 * Types of assertions that can be cached
 */
export type AssertionType =
  | 'text_present'
  | 'text_absent'
  | 'element_visible'
  | 'element_hidden'
  | 'evaluate'
  /** Driver-side, read-only Playwright statements returning a primitive value. */
  | 'playwright'
  | 'toast_present'
  /** A file matching condition was captured after the step began. */
  | 'download_present'
  /** A uniquely matching text download contains the expected text/pattern. */
  | 'download_content';

/**
 * Structured result of evaluating recipe-authored browser code.
 *
 * Only authored evidence may affect recipe health. Infrastructure failures
 * fail the active execution but are excluded from health accounting.
 */
export type AssertionEvaluationOutcome =
  | 'passed'
  | 'refuted'
  | 'authored_error'
  | 'infrastructure_error';

/**
 * Assertion specification for cache replay verification
 */
export type AssertionSpec = {
  type: AssertionType;
  /** The condition to check (text, selector, in-page expression, or Playwright statements) */
  condition: string;
  /** Expected value for evaluate/playwright assertions */
  expected?: unknown;
  /** Severity from an inline assertion: fatal halts the run, fail marks it failed but continues, warn logs only */
  severity?: AssertionSeverity;
  /** Source inline assertion ID for correlating results back to the node config */
  inlineAssertionId?: string;
  /** Short human-readable description supplied by the agent at browser_assert time */
  label?: string;
};

/**
 * Zod schema for `AssertionSpec` — single source for every surface that
 * accepts assertion specs from clients (the optimizer agent's commit tool and
 * the CLI cache-recipe route). The `satisfies` bind fails the build if the
 * schema and the documented type ever drift.
 */
export const assertionSpecSchema = z.object({
  type: z.enum([
    'text_present',
    'text_absent',
    'element_visible',
    'element_hidden',
    'evaluate',
    'playwright',
    'toast_present',
    'download_present',
    'download_content',
  ]),
  condition: z.string(),
  expected: z.unknown().optional(),
  severity: AssertionSeverityEnum.optional(),
  inlineAssertionId: z.string().optional(),
  label: z.string().optional(),
}) satisfies z.ZodType<AssertionSpec>;

/**
 * MCP node verify/extract snippet, stored ONCE per cache row in the
 * `codified_assertion` jsonb column (discriminated from browser
 * {@link AssertionSpec} rows by the `kind: 'mcp-verify'` key — AssertionSpec
 * has no `kind` field). The snippet is the agent-authored JS body executed by
 * `runMcpVerifySnippet` against the replayed tool-call results; it must return
 * `{ pass: boolean, reason?: string, outputs?: Record<string, unknown> }`.
 */
export type McpVerifyCodifiedAssertion = {
  kind: 'mcp-verify';
  /** Agent-authored JS function body (async IIFE-wrapped at execution). */
  code: string;
  /** Node-declared output names the snippet must return in `outputs`. */
  declaredOutputs?: string[];
};

export const mcpVerifyCodifiedAssertionSchema = z.object({
  kind: z.literal('mcp-verify'),
  code: z.string().min(1),
  declaredOutputs: z.array(z.string()).optional(),
}) satisfies z.ZodType<McpVerifyCodifiedAssertion>;

export function isMcpVerifyCodifiedAssertion(value: unknown): value is McpVerifyCodifiedAssertion {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { kind?: unknown }).kind === 'mcp-verify' &&
    typeof (value as { code?: unknown }).code === 'string'
  );
}

/**
 * What the `workflow_node_cache.codified_assertion` column stores: a browser
 * assertion spec (assertion/condition nodes) or the mcp verify snippet (mcp
 * nodes). Browser consumers must narrow via
 * {@link isMcpVerifyCodifiedAssertion} before reading AssertionSpec fields.
 */
export type CodifiedAssertion = AssertionSpec | McpVerifyCodifiedAssertion;

/**
 * Assertion payload attached to a composite recipe `run_code` action.
 *
 * Deliberately excludes ordinary cached-assert controls (`severity`,
 * `inlineAssertionId`, `strict`). A recipe oracle is part of the action's
 * success contract, never advisory, so replay has no field it could consult to
 * turn a refutation into a warning.
 */
export type CachedRecipeOracleAssertionSpec = {
  type: 'text_present' | 'text_absent' | 'element_visible' | 'element_hidden' | 'evaluate';
  condition: string;
  expected?: unknown;
  label?: string;
};

export type CachedRecipeOracle =
  | {
      kind: 'assertion';
      spec: CachedRecipeOracleAssertionSpec;
      frameHints?: string[];
    }
  | {
      kind: 'function';
      source: string;
    };

export const cachedRecipeOracleAssertionSpecSchema = z
  .object({
    type: z.enum(['text_present', 'text_absent', 'element_visible', 'element_hidden', 'evaluate']),
    condition: z.string().refine((value) => value.trim().length > 0, {
      message: 'Expected a non-empty string',
    }),
    expected: z.unknown().optional(),
    label: z.string().optional(),
  })
  .strict() satisfies z.ZodType<CachedRecipeOracleAssertionSpec>;

export const cachedRecipeOracleSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('assertion'),
      spec: cachedRecipeOracleAssertionSpecSchema,
      frameHints: z.array(z.string()).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('function'),
      source: z.string().refine((value) => value.trim().length > 0, {
        message: 'Expected a non-empty string',
      }),
    })
    .strict(),
]) satisfies z.ZodType<CachedRecipeOracle>;

/**
 * Evaluable assertion types usable for deterministic session validation. Derived from
 * {@link AssertionType} so it tracks automatically, minus two exclusions:
 * - `toast_present` — toasts depend on transient captured state that isn't present on
 *   a cold navigate to the session-check URL.
 * - `evaluate` — runs agent-authored JS via `page.evaluate` in the target app's origin
 *   and surfaces the result in logs; not worth that surface for a session-validity gate,
 *   and the four DOM types fully express "am I logged in?".
 */
export type AuthoredAssertionType = Exclude<
  AssertionType,
  'toast_present' | 'evaluate' | 'playwright' | 'download_present' | 'download_content'
>;

/**
 * A durable assertion the login agent authors after a successful login to prove the
 * logged-in state. Recorded on the credential's login cache and replayed
 * deterministically (via `IBrowserClient.assert`) on subsequent runs, so a valid
 * session can be confirmed without booting the AI session-validation agent. Re-authored
 * (overwritten) on every fresh AI login to stay current with the app's UI.
 *
 * Each authored assertion must PASS when the session is valid. Positive indicators
 * (e.g. an account menu) use `element_visible` / `text_present`; negative indicators
 * (e.g. a login form that should be gone once authenticated) use `element_hidden` /
 * `text_absent`, which pass quickly when the target is absent.
 */
export type AuthoredAssertion = {
  type: AuthoredAssertionType;
  /** Text content or CSS selector — same meaning as `AssertionSpec.condition` */
  condition: string;
  /** Short human-readable description of what this verifies */
  label: string;
};

/**
 * Zod schema for {@link AuthoredAssertion}. Shared by the login agent's
 * `report_login_result` tool input and the login-cache persistence layer so the
 * authored shape, the stored shape, and the evaluator agree.
 */
export const authoredAssertionSchema = z.object({
  type: z.enum(['text_present', 'text_absent', 'element_visible', 'element_hidden']),
  condition: z.string(),
  label: z.string(),
}) satisfies z.ZodType<AuthoredAssertion>;

/**
 * Stored login-cache validation payload (the `login_cache_validation_json` column).
 * Single source of truth for the jsonb shape, imported by the DB schema `$type`, the
 * credentials repo, and the session-check module so all three agree.
 *
 * `assertions` are the agent-authored session assertions. The legacy regex `*Indicators`
 * fields are kept optional only so pre-existing rows still deserialize — they are no
 * longer written or read.
 */
export type LoginCacheValidation = {
  authenticatedIndicators?: string[];
  unauthenticatedIndicators?: string[];
  /** Exact protected page where the agent authored `assertions`. */
  validationUrl?: string;
  assertions?: AuthoredAssertion[];
};

/**
 * Durable descriptor of the browser tab a cached action/step ran on, for
 * cache-durable multi-tab replay (Layer 4). It is a PREDICATE — a normalized
 * URL shape and/or a title substring — never a positional tab index (tab order
 * is not stable across runs, mirroring the positional-locator ban in
 * `locator-durability`). Absent ⇒ single-tab provenance ⇒ replay keeps today's
 * exact single-page behavior. Stamped at record time only when the browser
 * context has more than one open tab.
 */
export type DurableTabRef = {
  /**
   * Normalized URL shape of the active tab at record time
   * (via `normalizeUrlShapeForCacheReplay`), e.g. `https://app.example.com/orders/:uuid`.
   * Dynamic id segments are collapsed so the predicate survives fresh ids.
   */
  urlShape?: string;
  /**
   * Secret-safe hash of the full tab URL identity when query or hash data exists.
   * This lets replay reject query-only tab mismatches without storing token-bearing
   * query strings in cache rows or model-visible prompt state.
   */
  urlIdentityHash?: string;
  /** Document title of the active tab at record time; matched as a substring. */
  title?: string;
};

/**
 * Record-time confidence about which browser tab an action started on.
 *
 * `unique` is paired with {@link DurableTabRef}; `single` proves that exactly
 * one active tab was observed; `ambiguous` is a fail-closed marker that must
 * survive selector healing and force agent fallback. Omitted on legacy rows or
 * while the multi-tab feature is disabled.
 */
export type TabProvenance = 'single' | 'unique' | 'ambiguous';

/**
 * Agent-supplied evidence for the narrow cases where a positional locator is
 * genuinely the durable expression of the authored intent. This is admission
 * evidence only: it never grants verification credit, and the server binds it
 * to the exact compiled code before persistence. Coordinates need no evidence
 * (decision 2026-10-22). A stored legacy coordinate-only attestation fails
 * this parse; a `coordinate` key beside `positional` is stripped.
 */
export const cacheCodeDurabilityAttestationSchema = z.object({
  positional: z.discriminatedUnion(
    'reason',
    [
      z.object({
        reason: z.literal('single_visible_candidate'),
        observedCandidates: z.number().int().min(2),
        observedVisibleCandidates: z.literal(1),
        semanticScope: z.string().trim().min(3).max(240),
        evidence: z.string().trim().min(10).max(500),
      }),
      z.object({
        reason: z.literal('explicit_ordinal_intent'),
        ordinal: z.number().int().nonnegative(),
        authoredIntent: z.string().trim().min(1).max(240),
        evidence: z.string().trim().min(10).max(500),
      }),
    ],
    {
      error:
        'positional evidence (single_visible_candidate or explicit_ordinal_intent) is required; coordinates need no cacheCodeDurability, so omit it for coordinate-only code',
    }
  ),
});

export type CacheCodeDurabilityAttestation = z.infer<typeof cacheCodeDurabilityAttestationSchema>;

/** Server-bound durable form stored on the synthesized run_code action. */
export type BoundCacheCodeDurabilityAttestation = CacheCodeDurabilityAttestation & {
  /** SHA-256 of the exact `CachedAction.code` accepted with this evidence. */
  codeSha256: string;
};

/**
 * A single cached browser action with stable selectors
 */
export type CachedAction = {
  /** Sequence number within the cache */
  index: number;
  /** Original tool name from agent (e.g., 'browser_click') */
  toolName: string;
  /** Normalized action type category */
  actionType: CachedActionType;
  /** Workflow node this action belongs to */
  nodeId: string;
  /** Stable selector strategies (multiple for fallback) */
  selectors?: SelectorStrategy;
  /** Original tool arguments (sanitized - no ephemeral refs) */
  args: Record<string, unknown>;
  /** Assertion to verify during replay (for assertion nodes) */
  assertion?: AssertionSpec;
  /** Wait strategy to execute before this action */
  waitBefore?: WaitStrategy;
  /** Key from variable registry for variable-aware data substitution */
  variableKey?: string;
  /** Original element description for debugging */
  elementDescription?: string;
  /** Duration of original action in ms (for timing hints) */
  originalDurationMs?: number;
  /** Flag for actions that require agent fallback (missing selectors) */
  requiresAgent?: boolean;
  /** Who authored the active selector: deterministic or agentic */
  selectorAuthor?: SelectorAuthor;
  /** Why the AI locator agent failed, if attempted but unsuccessful */
  selectorAgentFailureReason?: string;
  /** True for injected annotations (vs browser actions) */
  isAnnotation?: boolean;
  /** Annotation type (e.g., 'step-completed', 'milestone') */
  annotationType?: string;
  /** Hex color or named color for annotation UI rendering */
  annotationColor?: string;
  /**
   * Maps arg paths to their original macro expressions.
   * At replay time, these are resolved fresh instead of using the literal in args.
   * e.g. { "text": "$UUID()", "fields.1.value": "user-$UUID()@test.com" }
   */
  macroBindings?: Record<string, string>;
  /**
   * Maps arg paths to variable registry keys for fill_form per-field substitution.
   * At replay time, these are resolved fresh from the variable bag.
   * e.g. { "fields.0.value": "company", "fields.1.value": "display_name" }
   */
  variableBindings?: Record<string, string>;
  /** For decomposed fill_form fields: the field index within the original fill_form */
  decomposedFieldIndex?: number;
  /**
   * Ref-less fast-drive only (`actionType === 'run_code'`): the self-contained
   * Playwright snippet the L2 agent authored to reproduce this step. Executed at
   * replay via the shared run_code primitive with fresh `vars`/`macro()`/`attach()`
   * bindings, so it carries no baked literals (no `macroBindings`/`variableBindings`).
   */
  code?: string;
  /**
   * Server-authored post-state contract for a composite interaction-recipe
   * `run_code` action. The code runs the recipe body and invokes the replay
   * oracle binding before returning, preserving one recipeRef → one action →
   * one classified step outcome.
   */
  recipeOracle?: CachedRecipeOracle;
  /**
   * Narrow durability exception for agent-authored positional code.
   * The strict gate accepts it only when `codeSha256` matches the exact current
   * code and no non-overridable anchor is present. It does not affect lifecycle
   * state: minted rows remain unverified until the normal prove pass succeeds.
   */
  durabilityAttestation?: BoundCacheCodeDurabilityAttestation;
  /**
   * Layer 4 cache-durable multi-tab replay: the durable descriptor of the tab
   * this action ran on, stamped at record time when the context had >1 open
   * tab. At replay the loop re-resolves the active page to this predicate before
   * executing the action. Absent ⇒ single-tab provenance ⇒ replay is unchanged.
   */
  activeTab?: DurableTabRef;
  /** Record-time tab confidence. See {@link TabProvenance}. */
  tabProvenance?: TabProvenance;
};

// ─────────────────────────────────────────────────────────────────────────────
// Cache Formation Diagnostics
// ─────────────────────────────────────────────────────────────────────────────

export type CacheFormationStepMatchStrategy =
  | 'actionRef'
  | 'heuristic'
  | 'agent'
  | 'cacheCode'
  | 'captureSaveAs'
  | 'assertionId'
  | 'retained'
  | 'gap'
  | 'missing';

export type CacheFormationPlaybookStepDiagnostic = {
  order: number;
  action: string;
  target: string;
  actionRefCount: number;
  resolvedActionRefCount: number;
  matchStrategy: CacheFormationStepMatchStrategy;
  matchedActionIndex?: number;
};

export type CacheFormationPlaybookDiagnostics = {
  stepCount: number;
  stepsWithActionRefs: number;
  stepsWithoutActionRefs: number;
  stepsWithResolvedActionRefs: number;
  stepsWithUnresolvedActionRefs: number;
  stepsMatchedByActionRef: number;
  stepsMatchedHeuristically: number;
  gapCount: number;
  steps: CacheFormationPlaybookStepDiagnostic[];
};

export type CacheFormationActionDiagnostic = {
  index: number;
  actionType: string;
  elementDescription?: string;
  hasSelectors: boolean;
  hasPlaywrightLocator: boolean;
  selectorAuthor?: SelectorAuthor;
  selectorAgentFailureReason?: string;
  requiresAgent: boolean;
  selectorTaskCount: number;
};

export type CacheFormationLocatorDiagnostics = {
  totalActions: number;
  selectorCapableActions: number;
  actionsWithSelectors: number;
  actionsWithPlaywrightLocator: number;
  agenticLocatorActions: number;
  deterministicLocatorActions: number;
  requiresAgentActions: number;
  actionsWithSelectorTasks: number;
  actions: CacheFormationActionDiagnostic[];
};

export type CacheFormationSelectorAgentTaskSource = 'live-queue' | 'proactive';

export type CacheFormationSelectorAgentTaskDiagnostic = {
  actionIndex: number;
  fieldIndex?: number;
  source: CacheFormationSelectorAgentTaskSource;
  hasHeuristicSelectors: boolean;
};

export type CacheFormationSelectorAgentStatus =
  | 'not-needed'
  | 'pending'
  | 'queued'
  | 'completed'
  | 'failed';

export type CacheFormationSelectorAgentDiagnostics = {
  liveQueueTaskCount: number;
  proactiveTaskCount: number;
  totalTaskCount: number;
  actionTaskCount: number;
  fieldTaskCount: number;
  bundleUploaded: boolean;
  drainJobEnqueued: boolean;
  status: CacheFormationSelectorAgentStatus;
  successCount?: number;
  failureCount?: number;
  failureReasons?: string[];
  tasks: CacheFormationSelectorAgentTaskDiagnostic[];
};

export type CacheFormationDiagnostics = {
  playbook: CacheFormationPlaybookDiagnostics;
  locators: CacheFormationLocatorDiagnostics;
  selectorAgent: CacheFormationSelectorAgentDiagnostics;
};

// ─────────────────────────────────────────────────────────────────────────────
// Cache Metadata Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Metadata about a workflow cache for eligibility and debugging
 */
export type WorkflowCacheMetadata = {
  /** Whether the inbox tool was used (disqualifies caching) */
  usedInboxTool: boolean;
  /** Total number of cached actions */
  actionCount: number;
  /** Node types that were executed */
  nodeTypes: string[];
  /** Whether all nodes completed successfully */
  allNodesSuccessful: boolean;
  /** Keys from inputValues used for dynamic data */
  inputValueKeys: string[];
};

// ─────────────────────────────────────────────────────────────────────────────
// Eligibility Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Result of cache eligibility check
 */
export type CacheEligibilityResult = { eligible: true } | { eligible: false; reason: string };

// ─────────────────────────────────────────────────────────────────────────────
// Replay Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Per-action timing breakdown for cache replay telemetry.
 * Captures where time is spent within each replayed action.
 */
export interface ReplayActionTiming {
  /** Action index within the replay sequence */
  index: number;
  /** Type of action (click, type, navigate, etc.) */
  actionType: string;
  /** Time spent in page stabilization (DOM delay + network idle + overlay check) */
  stabilizationMs: number;
  /** Time spent in pre-action wait strategy */
  waitStrategyMs: number;
  /** Time spent resolving the element (selector fallback chain + visibility wait) */
  resolutionMs: number;
  /** Subset of resolutionMs spent in verifyElementReady visibility wait */
  visibilityWaitMs: number;
  /** Subset of visibilityWaitMs spent waiting for position to stabilize (animations) */
  stabilityWaitMs: number;
  /** Time spent in the Playwright action itself (click, fill, etc.) */
  actionMs: number;
  /** Total wall-clock time for this action */
  totalMs: number;
  /** Whether this action was retried */
  retried?: boolean;
  /** Which selector strategy resolved the element */
  selectorUsed?: string;
  /**
   * Locator author that resolved (or attempted to resolve) the element.
   * 'deterministic' for role/testId/label/text/css strategies; 'agentic' for
   * the selector-agent-authored Playwright locator. Powers the cache-telemetry
   * deterministic-vs-agentic step success breakdown.
   */
  selectorAuthor?: SelectorAuthor;
  /**
   * Whether this action completed successfully during replay. False for the
   * action that triggered a replay failure; undefined when not classified.
   */
  actionSucceeded?: boolean;
  /** Short reason when actionSucceeded === false (mirrors the step cacheOutcomeReason). */
  failureReason?: string;
  /** Time spent waiting for a disabled element to become enabled (pre-click guard) */
  enabledWaitMs?: number;
  /** Pre-click actionability diagnostics (present when click fell back to coordinates) */
  clickDiagnostics?: {
    visible: boolean;
    enabled: boolean;
    stable: boolean;
    receivesPointerEvents: boolean;
    coveringElement?: { tag: string; className: string; id: string };
    /** True when pointer-event interception was detected before calling locator.click() */
    proactiveCoordinateClick?: boolean;
  };
  /** Debug-mode snapshot of the click target element state at click time (CACHE_REPLAY_DEBUG=true) */
  clickTargetState?: {
    tagName: string;
    outerHTML: string;
    disabled: boolean | null;
    ariaDisabled: string | null;
    className: string;
    id: string;
  };
}

/** Result of an inline assertion evaluation during replay */
export interface InlineAssertionResult {
  assertionId: string;
  condition: string;
  severity: AssertionSeverity;
  passed: boolean;
  reason?: string;
  source: 'cache' | 'agent';
  /** The assertion mechanism type, when available from the AssertionSpec */
  assertionType?: AssertionType;
  /** L2 verification verdict. When 'uncertain', Phase 3 cascades to L3 for re-evaluation. */
  verdict?: 'passed' | 'failed' | 'uncertain';
  /** Deterministic observed pass that could not be encoded after an attempted browser_assert. */
  downgradedFromDeterministic?: boolean;
  /**
   * True when this confirmed-failed verdict caused a cascade to a fresh agent
   * and is open to re-evaluation against the live DOM. Disputed results are
   * rendered in the agent prompt as "re-evaluate and report" instead of
   * "do NOT re-evaluate", and the fresh agent's verdict supersedes this one.
   *
   * EPHEMERAL prompt-composition hint: set only on the L2 assertion-eject
   * handoff path, consumed only by the prompt renderer, never persisted to
   * cache rows or step metadata. Do not gate runtime logic on it — the
   * runtime supersede keys off `source`, not this flag.
   */
  disputed?: boolean;
  /** Short human-readable description supplied by the agent at browser_assert time */
  label?: string;
}

/**
 * Normalized assertion result for non-blocking assertion accumulation.
 * Persisted to workflowRunSteps.metadataJson.assertionResults.
 */
export interface WorkflowAssertionResult {
  /** Assertion key assigned by the orchestrator (e.g., "assert-0", "assert-1") */
  key: string;
  /** Workflow node this assertion belongs to */
  nodeId: string;
  /** Workflow run step ID */
  stepId: string;
  /** Whether the assertion originated from a standalone assertion node or an inline assertion on an action node */
  origin: 'assertion_node' | 'inline_assertion';
  /** The human-readable assertion condition */
  condition: string;
  /** The assertion mechanism used */
  assertionType?: AssertionType | 'agentic';
  /** What a failure means: fatal halts the run, fail marks it failed but continues, warn logs only */
  severity: AssertionSeverity;
  /** Whether the assertion passed */
  passed: boolean;
  /**
   * False when the assertion was configured on the node but never evaluated
   * because the step failed before assertions ran. Omitted/true = evaluated.
   * Not-checked results are excluded from pass/warn/failure counts.
   */
  checked?: boolean;
  /** Human-readable reason for failure */
  reason?: string;
  /** How the assertion was evaluated */
  source: 'cache' | 'L2' | 'agent';
  /** ISO-8601 timestamp of evaluation */
  evaluatedAt: string;
  /** Source inline assertion ID for correlation back to node config */
  inlineAssertionId?: string;
  /** Short human-readable description supplied by the agent at browser_assert time */
  label?: string;
  /**
   * True when the agent judged a non-agentic-flagged inline assertion agentically,
   * because the condition is not programmatically assertable (e.g. vision-based
   * claim). Signal that the assertion should be re-authored as agentic; never set
   * by the browser/cache path.
   */
  downgradedFromDeterministic?: boolean;
  /**
   * Open issue linked to this assertion (`issues.subject_assertion_key === key`)
   * at evaluation time. Set only on failing results. An open linked issue is the
   * whole mechanism — there is no separate "known issue" concept.
   */
  issueId?: string;
}

/**
 * Summary of assertion results for a workflow run.
 * Persisted to workflowRuns.metadataJson.assertionSummary.
 * Derived from accumulated WorkflowAssertionResult records at finalization.
 */
export interface WorkflowAssertionSummary {
  total: number;
  passed: number;
  warnings: number;
  failures: number;
  /** Assertions configured on a failed step that were never evaluated (step broke before assertions ran). */
  notChecked?: number;
  /** True when any fail- or fatal-severity assertion failed (determines run failure) */
  hasFailSeverityFailures: boolean;
  /** Step ID of the first fail/fatal-severity failure (for UI navigation) */
  firstFailureStepId?: string;
  /**
   * True when the run failed ONLY because of assertions and every failing
   * assertion carries an open linked issue (`WorkflowAssertionResult.issueId`).
   * Written once at finalization, only ever `true`; absence means false. The
   * run still fails — this waives promotion/eligibility, never the result.
   */
  issueCovered?: boolean;
}

/**
 * Result of cache replay execution
 */
export type ReplayResult =
  | {
      success: true;
      evaluationOutcome: AssertionEvaluationOutcome;
      durationMs: number;
      actionTimings?: ReplayActionTiming[];
      /** Time spent in pre-replay page readiness wait (ms) */
      preReplayWaitMs?: number;
      /** Inline assertion results evaluated during replay (pass and fail) */
      assertionResults?: InlineAssertionResult[];
      /** Non-blocking capture failures for opportunistic captures */
      captureWarnings?: CacheCaptureWarning[];
    }
  | {
      success: false;
      evaluationOutcome: AssertionEvaluationOutcome;
      failedAtIndex: number;
      error: string;
      /** Whether to fall back to agent execution */
      shouldFallback: boolean;
      /** Classification of the failure for cache invalidation decisions */
      failureCategory?:
        | 'timing_hidden'
        | 'overlay_intercepted'
        | 'element_absent'
        | 'structural'
        | 'network'
        | 'other';
      /** Per-action timing for actions completed before failure */
      actionTimings?: ReplayActionTiming[];
      /** Time spent in pre-replay page readiness wait (ms) */
      preReplayWaitMs?: number;
      /** Inline assertion results collected before the failure */
      assertionResults?: InlineAssertionResult[];
      /** Capture warnings collected before the failure */
      captureWarnings?: CacheCaptureWarning[];
    };

/**
 * Element info extracted from accessibility snapshot
 */
export type ElementInfo = {
  /** Ephemeral ref from snapshot (e.g., 'e5') */
  ref: string;
  /** ARIA role */
  role?: string;
  /** Accessible name */
  accessibleName?: string;
  /** Text content */
  textContent?: string;
  /** Text segments (individual visible text nodes) */
  textSegments?: string[];
  /** Aria label attribute */
  ariaLabel?: string;
  /** Label text (from <label>, aria-labelledby, or aria-label) */
  labelText?: string;
  /** Placeholder text (inputs) */
  placeholder?: string;
  /** Title attribute */
  title?: string;
  /** Alt text (images) */
  altText?: string;
  /** Form field name attribute */
  nameAttr?: string;
  /** data-testid or similar */
  dataTestId?: string;
  /** HTML tag name */
  tagName?: string;
  /** CSS class name */
  className?: string;
  /** Element ID */
  id?: string;
  /** Parent element role for context */
  parentRole?: string;
  /** Parent element name for context */
  parentName?: string;
  /** True when parentName was extracted from a child heading, not the parent's own accessible name */
  parentNameFromHeading?: boolean;
  /** Center X coordinate (from enriched snapshot) */
  centerX?: number;
  /** Center Y coordinate (from enriched snapshot) */
  centerY?: number;
  /** Frame context if element is inside an iframe or shadow DOM */
  frameContext?: FrameContext;

  // ─────────────────────────────────────────────────────────────────────────────
  // Data attributes for disambiguation
  // ─────────────────────────────────────────────────────────────────────────────

  /** data-item-index or data-selection-index for list items */
  itemIndex?: number;
  /** aria-rowindex for grid/table rows */
  ariaRowIndex?: number;
  /** data-automationid for Microsoft controls */
  automationId?: string;
  /** Generic data-* attributes for disambiguation */
  dataAttributes?: Record<string, string>;

  /** Child img alt text for disambiguation of generic containers */
  childImgAlt?: string;
};
